import { Box, Flex, IconButton, useBreakpoint, useDisclosure, VisuallyHidden } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FilterTagButton } from "../../components/buttons/FilterTag";
import { BrowseFilters } from "./components/filters/BrowseFilters.tsx";
import { BrowseFiltersModal } from "./components/filters/BrowseFiltersModal.tsx";
import { BrowseFiltersSidebar } from "./components/filters/BrowseFiltersSidebar.tsx";
import { BrowseResultsGrid } from "./components/BrowseResultsGrid.tsx";
import { BrowseTabs, FloatingBrowseTab } from "./components/BrowseTabs.tsx";
import {
  useIsSmallBreakpoint,
  useMobileBreakpoint,
} from "../../utils/useBreakpoints.ts";
import { useRemoveQueries } from "../../services/axiosInstance.ts";
import {
  GET_ARTIST_LIST_QUERY_KEY,
  GET_COMIC_LIST_QUERY_KEY,
  GET_RELEASES_LIST_QUERY_KEY,
  GET_BUNDLES_LIST_QUERY_KEY,
} from "./api/browseScreenApi.ts";
import { useHookstate } from "@hookstate/core";
import { t } from "i18next";
import { ScreenLayout } from "../../components/layouts/components/ScreenLayout.tsx";
import { ModeToggle } from "../../components/resultsGrid/ModeToggle.tsx";
import { LayoutStyle, BrowseTabType } from "../../types.ts";
import { SortMenus } from "../../components/sortMenus/SortMenus.tsx";
import { useScrollVisibility } from "../../hooks/useScrollVisibility.ts";
import { CollapsibleNavBar } from "../../components/navbar/CollapsibleNavBar.tsx";
import { useBrowseFiltersAPI } from "./hooks/useBrowseFiltersAPI.ts";
import { throttle } from "lodash-es";
import { useTitleOnNavigation } from "../../hooks/useTitleOnNavigation.ts";
import { appStore } from "../../hookstate/appStore.ts";
import { FilterState, buildUrlPath, buildSearchParams } from "./utils/browseUrlParser.ts";
import { useTopBarHeight } from "../../hooks/userTopBarHeight.ts";
import { useAccountData } from "../../hooks/useAccountData.ts";
import { useNavigate, useLocation } from "react-router-dom";
import { useVirtualScrollRestoration } from "../../hooks/useVirtualScrollRestoration.ts";
import { useBrowseFilters } from "./hooks/useBrowseFilters.ts";
import { useSearchV2API } from "./hooks/useSearchV2API.ts";
import { useSearchV2 } from "../search/hooks/useSearchContext.tsx";
import { isChannelContext } from "../search/types.ts";
import { FilterIcon } from "../../components/icons/FilterIcon.tsx";

const relevanceOrder = [{ id: "relevance", name: "Relevance" }];

export default function BrowseScreen() {
  const isSmall = useIsSmallBreakpoint();
  const isMobile = useMobileBreakpoint();
  const topBarHeight = useTopBarHeight();
  const accountData = useAccountData();
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll restoration - different tabs/filters = different scroll positions
  // Normalize search params by sorting to ensure consistent key regardless of param order
  const normalizedSearch = new URLSearchParams(location.search);
  normalizedSearch.sort();
  const { onVisibleIndexChange, restoreToIndex } = useVirtualScrollRestoration({
    keySegment: normalizedSearch.toString(),
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const breakpoint = useBreakpoint({ ssr: false });
  const isXlUp = ["xl", "2xl", "3xl", "4xl"].includes(breakpoint);
  const { isOpen: isSidebarOpen, onToggle: onSidebarToggle, onClose: onSidebarClose, onOpen: onSidebarOpen } = useDisclosure();
  const sidebarInitialized = useRef(false);
  useEffect(() => {
    if (!sidebarInitialized.current && breakpoint) {
      sidebarInitialized.current = true;
      if (isXlUp) onSidebarOpen();
    }
  }, [breakpoint]);

  // Derive tab directly from React Router location (not state)
  const tab = useMemo((): BrowseTabType => {
    const pathSegment = location.pathname.split("/")[1];
    switch (pathSegment) {
      case "creators":
        return BrowseTabType.creators;
      case "books":
        return BrowseTabType.releases;
      case "bundles":
        return BrowseTabType.bundles;
      default:
        return BrowseTabType.comics;
    }
  }, [location.pathname]);

  // URL-derived filter state
  const {
    filters,
    apiParams,
    filterMaps,
    setFilters,
    clearAllFilters,
    slugEntities,
    ready,
  } = useBrowseFilters(tab);

  // Navigate to a new tab, preserving current filters
  const setTab = useCallback(
    (newTab: BrowseTabType) => {
      if (!filterMaps || !filters) {
        const tabPaths: Record<BrowseTabType, string> = {
          [BrowseTabType.comics]: "/browse",
          [BrowseTabType.releases]: "/books",
          [BrowseTabType.creators]: "/creators",
          [BrowseTabType.bundles]: "/bundles",
        };
        // Preserve browse_key even in fallback path
        const currentParams = new URLSearchParams(location.search);
        const browseKey = currentParams.get("browse_key");
        const fallbackSearch = browseKey ? `?browse_key=${browseKey}` : "";
        navigate(`${tabPaths[newTab]}${fallbackSearch}`);
        return;
      }
      const path = buildUrlPath(filters, filterMaps, newTab);
      const params = buildSearchParams(filters, filterMaps, newTab);

      // Preserve params not managed by the filter system
      const currentParams = new URLSearchParams(location.search);
      for (const key of ["standalone", "browse_key"]) {
        const val = currentParams.get(key);
        if (val) params.set(key, val);
      }

      params.sort();
      const search = params.toString();
      navigate(search ? `${path}?${search}` : path);
    },
    [navigate, filters, filterMaps, location.search],
  );
  const [mode, setMode] = useState<LayoutStyle["mode"]>("grid");

  const [orders, setOrders] = useState<
    Array<{
      id: string;
      name: string;
    }>
  >([]);

  const removeQueries = useRemoveQueries(
    (() => {
      switch (tab) {
        case BrowseTabType.comics:
          return GET_COMIC_LIST_QUERY_KEY;
        case BrowseTabType.releases:
          return GET_RELEASES_LIST_QUERY_KEY;
        case BrowseTabType.bundles:
          return GET_BUNDLES_LIST_QUERY_KEY;
        default:
          return GET_ARTIST_LIST_QUERY_KEY;
      }
    })(),
  );

  let searchPlaceHolder = "";

  if (tab === BrowseTabType.comics) {
    searchPlaceHolder = t("screens.browse.searchPlaceholderComics");
  } else if (tab === BrowseTabType.releases) {
    searchPlaceHolder = t("screens.browse.searchPlaceholderReleases");
  } else if (tab === BrowseTabType.creators) {
    searchPlaceHolder = t("screens.browse.searchPlaceholderCreators");
  } else if (tab === BrowseTabType.bundles) {
    searchPlaceHolder = "Search bundles...";
  }

  const handleClearAll = useCallback(() => {
    clearAllFilters();
    removeQueries?.();
  }, [clearAllFilters, removeQueries]);

  useEffect(() => {
    if (!isSmall && mode === "list") {
      setMode("grid");
    }
  }, [isSmall]);

  const appInitSortingOrders = useHookstate(
    appStore.init.comic_browse_sort_orders,
  ).get({
    noproxy: true,
  });


  const { isHidden } = useScrollVisibility();

  const isSearchV2 = !!accountData?.features?.["search_v2"];
  const v2Desktop = isSearchV2 && !isSmall;
  const v2Mobile = isSearchV2 && isSmall;
  const sidebarMode = isXlUp ? "push" as const : "overlay" as const;
  const searchQuery = filters?.query ?? "";
  const isSearching = isSearchV2 && searchQuery.length > 0;

  // Channel context: merge channel filters (e.g. artist_id) into apiParams
  const { context: activeContext } = useSearchV2();
  const channelFilters = isChannelContext(activeContext) ? activeContext.filters : undefined;
  const mergedApiParams = useMemo(() => {
    if (!apiParams || !channelFilters) return apiParams;
    const merged = new URLSearchParams(apiParams);
    for (const [key, value] of Object.entries(channelFilters)) {
      merged.set(key, String(value));
    }
    return merged;
  }, [apiParams, channelFilters]);

  // Wait for account/me before deciding which data source to use,
  // so the search_v2 feature flag is known and we don't flip mid-flight.
  const accountReady = accountData !== undefined;
  // V2: unified search endpoint handles both browse and search
  // V1: old browse endpoints only
  const browseEnabled = ready && accountReady && !isSearchV2;
  const searchEnabled = isSearchV2 && ready && accountReady;

  const {
    ready: apiReady,
    data: browseData,
    isLoading: browseLoading,
    isFetchingNextPage: browseFetchingNext,
    hasNextPage: browseHasNext,
    fetchNextPage: browseFetchNext,
  } = useBrowseFiltersAPI(isOpen, tab, mergedApiParams, browseEnabled);

  const {
    data: searchData,
    isLoading: searchLoading,
    isFetchingNextPage: searchFetchingNext,
    hasNextPage: searchHasNext,
    fetchNextPage: searchFetchNext,
    counts: searchCounts,
  } = useSearchV2API(tab, searchQuery, searchEnabled, mergedApiParams);

  // Pick the active data source
  const data = (searchEnabled ? searchData : browseData) as typeof browseData;
  const isLoading = searchEnabled ? searchLoading : browseLoading;
  const isFetchingNextPage = searchEnabled ? searchFetchingNext : browseFetchingNext;
  const hasNextPage = searchEnabled ? searchHasNext : browseHasNext;
  const fetchNextPage = searchEnabled ? searchFetchNext : browseFetchNext;


  // Update sort orders when ready, search query, or tab changes
  useEffect(() => {
    if (!ready) return;

    let baseOrders = appInitSortingOrders;

    // Filter out Featured and Recent options when Releases tab is selected
    if (tab === BrowseTabType.releases) {
      baseOrders = appInitSortingOrders.filter(
        (order) => order.name.toLowerCase() !== "featured" && order.name.toLowerCase() !== "recent",
      );
    }

    if (searchQuery) {
      setOrders([...baseOrders, ...relevanceOrder]);
    } else {
      setOrders([...baseOrders]);
    }
  }, [ready, searchQuery, tab, appInitSortingOrders]);

  const throttleSearch = useCallback(
    throttle((term: string) => {
      if (!ready) return;
      setFilters({ query: term });
    }, isSearchV2 ? 300 : 1000),
    [ready, setFilters, isSearchV2],
  );

  const handleSortOrderChange = useCallback(
    (value: string) => {
      setFilters({ sortOrder: value });
    },
    [setFilters],
  );

  const handleSortDurationChange = useCallback(
    (value: string) => {
      setFilters({ sortDuration: value });
    },
    [setFilters],
  );

  const handleModalApply = useCallback(
    (draft: FilterState) => {
      setFilters(draft);
    },
    [setFilters],
  );

  // Shared grid component
  const resultsGrid = (
    <BrowseResultsGrid
      header={
        v2Desktop ? (
          // V2 Desktop header: tabs + sort + filter icon in one row, no ScreenLayout
          <CollapsibleNavBar
            isCollapsed={false}
            shouldCollapse={false}
            boxProps={{
              position: "sticky",
              top: `${topBarHeight.get()}px`,
              zIndex: 1,
              bg: "charcoal.charcoal",
            }}
          >
            {/* Keep page title for document title, but visually hidden */}
            <VisuallyHidden>
              <BrowsePageTitle
                tab={tab}
                ready={ready}
                slugEntities={slugEntities}
                searchQuery={isSearching ? searchQuery : undefined}
              />
            </VisuallyHidden>
            <Flex
              justifyContent="space-between"
              align="flex-end"
              paddingTop="16px"
              marginBottom="16px"
              borderBottom="1.5px solid"
              borderColor="transparent.white.20"
              pr={isXlUp ? "4px" : "16px"}
              mr={isXlUp ? "-16px" : "0px"}
            >
              <BrowseTabs
                active={tab}
                setActive={setTab}
                counts={isSearching ? searchCounts ?? undefined : undefined}
              />
              <Flex align="center" gap="8px" pb="12px">
                <SortMenus
                  invalidate={removeQueries}
                  sortOrder={filters?.sortOrder}
                  sortDuration={filters?.sortDuration}
                  onSortOrderChange={handleSortOrderChange}
                  onSortDurationChange={handleSortDurationChange}
                  sortOrders={orders}
                />
                <IconButton
                  aria-label="Toggle filters"
                  icon={<FilterIcon boxSize="20px" />}
                  variant="ghost"
                  color={isSidebarOpen ? "blaze.300" : "neutral.400"}
                  size="sm"
                  w="34px"
                  h="34px"
                  borderRadius="8px"
                  _hover={{ bg: "transparent.white.10" }}
                  onClick={onSidebarToggle}
                />
              </Flex>
            </Flex>
          </CollapsibleNavBar>
        ) : v2Mobile ? (
          // V2 Mobile header: row 1 (tabs), row 2 (sort + mode toggle + filter icon)
          <CollapsibleNavBar
            isCollapsed={isHidden && !isSidebarOpen}
            shouldCollapse={!isSidebarOpen}
            boxProps={{
              px: "16px",
              pb: "8px",
              position: "sticky",
              top: `${topBarHeight.get()}px`,
              zIndex: 1,
              bg: "charcoal.charcoal",
            }}
          >
            <VisuallyHidden>
              <BrowsePageTitle
                tab={tab}
                ready={ready}
                slugEntities={slugEntities}
                searchQuery={isSearching ? searchQuery : undefined}
              />
            </VisuallyHidden>
            {/* Row 1: Tabs */}
            <Flex
              align="flex-end"
              borderBottom="1.5px solid"
              borderColor="transparent.white.20"
            >
              <BrowseTabs
                active={tab}
                setActive={setTab}
                counts={isSearching ? searchCounts ?? undefined : undefined}
              />
            </Flex>
            {/* Row 2: Sort + Grid toggle + Filter icon */}
            <Flex align="center" justify="space-between" py="12px">
              <Flex align="center" gap="8px">
                <SortMenus
                  invalidate={removeQueries}
                  sortOrder={filters?.sortOrder}
                  sortDuration={filters?.sortDuration}
                  onSortOrderChange={handleSortOrderChange}
                  onSortDurationChange={handleSortDurationChange}
                  sortOrders={orders}
                />
                <ModeToggle mode={mode} setMode={setMode} />
              </Flex>
              <IconButton
                aria-label="Open filters"
                icon={<FilterIcon boxSize="20px" />}
                variant="ghost"
                w="34px"
                h="34px"
                minW="34px"
                borderRadius="md"
                color="neutral.400"
                _hover={{ bg: "transparent.white.10" }}
                onClick={onSidebarToggle}
              />
            </Flex>
          </CollapsibleNavBar>
        ) : (
          // V1 header: unchanged
          <CollapsibleNavBar
            isCollapsed={isHidden}
            shouldCollapse={isSmall}
            boxProps={{
              paddingRight: isSmall ? "0px" : "40px",
              position: "sticky",
              top: `${topBarHeight.get()}px`,
              zIndex: 1,
              bg: "charcoal.charcoal",
            }}
          >
            <ScreenLayout
              isTitleHidden={isSmall && isHidden}
              pageTitle={
                <BrowsePageTitle
                  tab={tab}
                  ready={ready}
                  slugEntities={slugEntities}
                  searchQuery={isSearching ? searchQuery : undefined}
                />
              }
              initValue={isSearchV2 ? undefined : (filters?.query ?? "")}
              onSearch={isSearchV2 ? undefined : throttleSearch}
              searchPlaceholder={searchPlaceHolder}
              hideSearch={isSearchV2}
            />
            {!isSmall && (
              <Flex direction="row" gap="16px">
                <Flex gap="6px">
                  {filters && (
                    <BrowseFiltersModal
                      filters={filters}
                      onApply={handleModalApply}
                      isOpen={isOpen}
                      onOpen={onOpen}
                      onClose={onClose}
                      type={tab}
                      invalidate={removeQueries}
                    />
                  )}
                  <FilterTagButton
                    alt={true}
                    onClick={handleClearAll}
                    text="Clear all"
                  />
                </Flex>
                {filters && (
                  <BrowseFilters
                    invalidate={removeQueries}
                    frozen={isOpen}
                    tab={tab}
                    filters={filters}
                    onFilterChange={setFilters}
                  />
                )}
              </Flex>
            )}
            <Flex
              justifyContent="space-between"
              paddingTop={isSmall ? "0px" : "16px"}
              paddingBottom={isMobile ? "16px" : undefined}
              px={isSmall ? "16px" : "0px"}
              marginBottom="16px"
              boxShadow="inset 0px -1.5px 0px var(--chakra-colors-transparent-white-20)"
            >
              {!isSmall && (
                <BrowseTabs
                  active={tab}
                  setActive={setTab}
                  counts={isSearching ? searchCounts ?? undefined : undefined}
                />
              )}
              <SortMenus
                invalidate={removeQueries}
                sortOrder={filters?.sortOrder}
                sortDuration={filters?.sortDuration}
                onSortOrderChange={handleSortOrderChange}
                onSortDurationChange={handleSortDurationChange}
                sortOrders={orders}
              />
              {isMobile && <ModeToggle mode={mode} setMode={setMode} />}
            </Flex>
          </CollapsibleNavBar>
        )
      }
      tab={tab}
      mode={mode}
      gridHeightAdjustment={
        isHidden && isSmall ? "0px" : `${topBarHeight.get()}px`
      }
      data={data}
      isLoading={isLoading}
      urlSettled={searchEnabled || apiReady}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onVisibleIndexChange={onVisibleIndexChange}
      restoreToIndex={restoreToIndex}
      isSearchV2={isSearchV2}
      isSearching={isSearching}
    />
  );

  // V2 Desktop layout: grid + sidebar in a row
  if (v2Desktop) {
    return (
      <Flex
        direction="column"
        width="full"
        sx={{ WebkitOverflowScrolling: "touch" }}
      >
        <Flex gap="16px">
          <Box flex="1" minW={0}>
            {resultsGrid}
          </Box>
          {filters && (
            <BrowseFiltersSidebar
              tab={tab}
              filters={filters}
              onFilterChange={setFilters}
              onClose={onSidebarClose}
              onClearAll={handleClearAll}
              isOpen={isSidebarOpen}
              topOffset={topBarHeight.get()}
              mode={sidebarMode}
            />
          )}
        </Flex>
      </Flex>
    );
  }

  // V2 Mobile layout: grid + fullscreen filter sidebar (no floating tabs)
  if (v2Mobile) {
    return (
      <Flex
        direction="column"
        gap={0}
        width="full"
        sx={{ WebkitOverflowScrolling: "touch" }}
      >
        {resultsGrid}
        {filters && (
          <BrowseFiltersSidebar
            tab={tab}
            filters={filters}
            onFilterChange={setFilters}
            onClose={onSidebarClose}
            onClearAll={handleClearAll}
            isOpen={isSidebarOpen}
            topOffset={topBarHeight.get()}
            mode="fullscreen"
          />
        )}
      </Flex>
    );
  }

  // V1 layout: unchanged
  return (
    <Flex
      direction="column"
      gap={isSmall ? 0 : 6}
      width="full"
      sx={{
        WebkitOverflowScrolling: "touch",
      }}
    >
      {resultsGrid}
      {isSmall && (
        <Flex
          position="fixed"
          width="100%"
          justifyContent="center"
          left={0}
          bottom="20px"
          gap="8px"
        >
          <FloatingBrowseTab active={tab} setActive={setTab} />
          {filters && (
            <BrowseFiltersModal
              filters={filters}
              onApply={handleModalApply}
              isOpen={isOpen}
              onOpen={onOpen}
              onClose={onClose}
              type={tab}
              small
              invalidate={removeQueries}
            />
          )}
        </Flex>
      )}
    </Flex>
  );
}

interface BrowsePageTitleProps {
  tab: BrowseTabType;
  ready: boolean;
  slugEntities: Record<string, any>;
  searchQuery?: string;
}

function BrowsePageTitle({ tab, ready, slugEntities, searchQuery }: BrowsePageTitleProps) {
  const title = searchQuery
    ? `Results for "${searchQuery}"`
    : determineBrowsePageTitle(tab, ready, slugEntities);
  useTitleOnNavigation(title);
  return title;
}

function determineBrowsePageTitle(
  tab: BrowseTabType,
  ready: boolean,
  slugEntities: Record<string, any>,
): string | null {
  const {
    lang: language,
    type,
    genre: category,
  } = slugEntities ?? {};

  if (tab === BrowseTabType.releases) {
    return t("screens.browse.releases_title");
  } else if (tab === BrowseTabType.creators) {
    return t("screens.browse.creators_title");
  } else if (tab === BrowseTabType.bundles) {
    return "Bundles";
  }

  const props = {
    language,
    category,
    type,
  };

  if ((!language && !category && !type) || !ready) {
    return t("screens.browse.browse_title", props);
  } else if (language && category && type) {
    return t("screens.browse.browse_title_type_category_language", props);
  } else if (category && type) {
    return t("screens.browse.browse_title_type_category", props);
  } else if (type && language) {
    return t("screens.browse.browse_title_type_language", props);
  } else if (category && language) {
    return t("screens.browse.browse_title_category_language", props);
  } else if (language) {
    return t("screens.browse.browse_title_language", props);
  } else if (category) {
    return t("screens.browse.browse_title_category", props);
  } else if (type) {
    return t("screens.browse.browse_title_type", props);
  }

  return null;
}
